import clsx from 'clsx';
import { uniqueId } from 'lodash-es';
import {
  type ComponentProps,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useMemo,
  Children,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import Borders from '@dynatrace/strato-design-tokens/borders';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import {
  ChevronDownSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { getRowDetailsRowModel } from './details-row-model.js';
import { rowDetailsWrapperCSS } from './RowDetails.sty.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type {
  ColumnDef,
  InitialTableState,
  OnChangeFn,
  Row,
  RowModel,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * Props for the `DataTableV2.ExpandableRow` template slot.
 * @public
 */
export interface DataTableV2ExpandableRowBaseProps<
  TData extends DataTableV2RowData,
> {
  /**
   * Define a render function as the child of this slot selection to
   * control what is rendered in the expanded row section.
   */
  children: (params: { row: TData }) => ReactNode;

  /**
   * Callback triggered when the state of the expanded row is changed.
   */
  onExpandedRowsChange?: (expandedRows: Record<string, boolean>) => void;

  /**
   * Allows you to disable individual rows from expanding by returning true from the
   * passed function.
   */
  disableExpand?: (row: TData) => boolean;
}

/**
 * @public
 */
export interface DataTableV2ExpandableRowControlledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2ExpandableRowBaseProps<TData> {
  /**
   * Controlled state for the expanded rows of the DataTableV2.
   */
  expandedRows?: true | Record<string, boolean>;
  /**
   * Uncontrolled state for the expanded rows of the DataTableV2.
   */
  defaultExpandedRows?: never;
}

/**
 * @public
 */
export interface DataTableV2ExpandableRowUncontrolledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2ExpandableRowBaseProps<TData> {
  /**
   * Controlled state for the expanded rows of the DataTableV2.
   */
  expandedRows?: never;
  /**
   * Uncontrolled state for the expanded rows of the DataTableV2.
   */
  defaultExpandedRows?: true | Record<string, boolean>;
}

/**
 * @public
 */
export type DataTableV2ExpandableRowProps<TData extends DataTableV2RowData> =
  | DataTableV2ExpandableRowControlledProps<TData>
  | DataTableV2ExpandableRowUncontrolledProps<TData>;

/**
 * DataTableV2 slot selection for expandable rows.
 * @public
 */
export function DataTableV2ExpandableRowTemplate<
  TData extends DataTableV2RowData,
>(props: DataTableV2ExpandableRowProps<TData>) {
  return null;
}

/**
 * Wrapper helper props for the DataTableV2.ExpandableRow.
 * @public
 */
export interface DataTableV2ExpandableRowWrapperProps
  extends PropsWithChildren,
    ComponentProps<'div'> {}

/**
 * Wrapper helper for the DataTableV2.ExpandableRow that will automatically sync
 * specific style properties like row padding for rowDensity variant settings.
 * @public
 */
export function DataTableV2ExpandableRowWrapper(
  props: DataTableV2ExpandableRowWrapperProps,
) {
  const { className: consumerClassName, ...remainingProps } = props;
  const table = useDataTableV2();
  return (
    <div
      {...remainingProps}
      className={clsx(
        consumerClassName,
        rowDetailsWrapperCSS({
          rowDensity: table.getVisualVariantOptions().rowDensity,
        }),
      )}
    />
  );
}

/**
 * Structure for the expanded row state of the DataTableV2.
 * @internal
 */
type DataTableV2RowDetailState = true | Record<string, boolean>;

/**
 * Extensions interface for the tanstack table state.
 * @internal
 */
export interface DataTableV2RowDetailsState {
  rowDetails?: DataTableV2RowDetailState;
}

/**
 * Type guard to ensure that a react element is a `DataTableV2.ExpandableRow` element.
 * @internal
 */
function isDataTableV2ExpandableRowTemplate<TData extends DataTableV2RowData>(
  child: unknown,
): child is ReactElement<DataTableV2ExpandableRowProps<TData>> {
  return isElement(child) && child.type === DataTableV2ExpandableRowTemplate;
}

/**
 * Extension interface to extend the types from the tanstack table options.
 * @internal
 */
export interface DataTableV2RowDetailsOptions<
  TData extends DataTableV2RowData,
> {
  /**
   * Determines if the row details feature is enabled for this table instance.
   */
  enableRowDetails?: true;
  /**
   * Stores the rowDetails template for rendering in the expandable.
   */
  rowDetailsTemplate?: (params: { row: TData }) => ReactNode;
  /**
   * Callback when the row details state changes.
   */
  onRowDetailsOpenChange?: OnChangeFn<DataTableV2RowDetailState>;
  /**
   * RowModel option for the extended Details row model.
   */
  getRowDetailsRowModel?: (table: Table<TData>) => () => RowModel<TData>;
  /**
   * Callback to check if a row is disabled or not.
   */
  rowDetailsDisableRow?: (row: TData) => boolean;
}

/**
 * Symbol used to identify a row as a details row.
 * @internal
 */
export const DETAILS_ROW_IDENTIFIER = Symbol('DETAILS_ROW_IDENTIFIER');

/**
 * Appended to row id of cloned expanded row.
 * @internal
 */
export const ROW_EXPANDED_ID_SUFFIX = '-expanded';

/**
 * Symbol used to identify a rows parity.
 * @internal
 */
export const DETAILS_ROW_PARITY = Symbol('DETAILS_ROW_PARITY');

/**
 * Extension interface to extend the types from the tanstack table instance.
 * @internal
 */
export interface DataTableV2RowDetailsInstance<
  TData extends DataTableV2RowData,
> {
  /**
   * Internal identification id for the table instance to connect aria-controls
   * between the expander button and the row details.
   * @internal
   */
  _rowDetailsTableId: string;

  /**
   * Internal storage for the row model including the row details rows.
   * @internal
   */
  _getRowDetailsRowModel?: () => RowModel<TData>;
  /**
   * Allows access to the row details model if available.
   */
  getRowDetailsRowModel: () => RowModel<TData>;
  /**
   * Allows access to the row model coming before the row details.
   */
  getPreRowDetailsRowModel: () => RowModel<TData>;
}

/**
 * @internal
 * Extension interface to extend the types from the tanstack table instance.
 */
export interface DataTableV2RowDetailsRow {
  /**
   * Determines if the details row for a particular row is open.
   */
  getIsDetailsOpen: () => boolean;

  /**
   * Determines if the details for this row can be shown.
   */
  getCanOpenDetails: () => boolean;

  /**
   * Control command to toggle the state of the rowDetails.
   */
  toggleDetails: () => void;

  /**
   * Defines the grid position for the row details row.
   */
  getRowDetailsGridPosition: () => {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}`;
  };

  /**
   * Returns the aria control id to connect the expanding button to the
   * row details wrapper.
   */
  getRowDetailsAriaControlsId: () => string;

  /**
   * Identifier for the row parity in case of details row this is set
   * in the row model.
   * @internal
   */
  [DETAILS_ROW_PARITY]?: 'even' | 'odd';

  /**
   * Identifier if the row is a details row.
   * @internal
   */
  [DETAILS_ROW_IDENTIFIER]?: boolean;
}

/**
 * Row details feature for the DataTableV2.
 * @internal
 */
export const DataTableV2RowDetails: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      rowDetails: {},
      ...state,
    };
  },

  createTable<TData extends DataTableV2RowData>(table: Table<TData>) {
    /**
     * Internal identification id for the table instance to connect aria-controls
     * between the expander button and the row details.
     * @internal
     */
    table._rowDetailsTableId = uniqueId('DataTableV2-');

    /**
     * Allows access to the row model coming before the row details.
     */
    table.getPreRowDetailsRowModel = () => table.getPaginationRowModel();

    /**
     * Allows access to the row details model if available.
     */
    table.getRowDetailsRowModel = () => {
      if (
        !table._getRowDetailsRowModel &&
        table.options.getRowDetailsRowModel
      ) {
        table._getRowDetailsRowModel =
          table.options.getRowDetailsRowModel(table);
      }

      if (!table._getRowDetailsRowModel) {
        return table.getPreRowDetailsRowModel();
      }

      return table._getRowDetailsRowModel();
    };
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    /**
     * Determines if the details row for a particular row is open.
     */
    row.getIsDetailsOpen = () => {
      const id = row.id;
      const detailOpenState = table.getState().rowDetails ?? false;

      if (typeof detailOpenState === 'boolean') {
        return detailOpenState;
      }
      return detailOpenState[id] === true;
    };

    /**
     * Defines the grid position for the row details row.
     */
    row.getRowDetailsGridPosition = () => {
      return {
        gridColumnStart: 1,
        gridColumnEnd: `span ${table.getVisibleLeafColumns().length}`,
      };
    };

    /**
     * Returns the aria control id to connect the expanding button to the
     * row details wrapper.
     */
    row.getRowDetailsAriaControlsId = () => {
      return `${table._rowDetailsTableId}-${row.id}-expand`;
    };

    /**
     * Determines if the details for this row can be shown.
     */
    row.getCanOpenDetails = () => {
      if (typeof table.options.rowDetailsDisableRow === 'function') {
        return !table.options.rowDetailsDisableRow(row.original);
      }
      return true;
    };

    /**
     * Control command to toggle the state of the rowDetails.
     */
    row.toggleDetails = () => {
      // remember current scroll position
      const scrollOffset = table.getRowScrollOffset();

      // expand/collapse details
      table.options.onRowDetailsOpenChange?.((prev) => {
        if (typeof prev === 'boolean' && prev === true) {
          const flatRows = table.getRowModel().flatRows;
          const newRowDetailsState: DataTableV2RowDetailState = {};

          for (const flatRow of flatRows) {
            if (row.getCanOpenDetails() && row.id !== flatRow.id) {
              newRowDetailsState[flatRow.id] = true;
            }
          }
          return newRowDetailsState;
        } else {
          const newRowDetailsState: Record<string, boolean> = {
            ...prev,
          };
          if (newRowDetailsState[row.id] === true) {
            delete newRowDetailsState[row.id];
          } else {
            newRowDetailsState[row.id] = true;
          }
          return newRowDetailsState;
        }
      });

      // scroll back to original scroll position to avoid row jumping
      requestAnimationFrame(() => {
        table.scrollToRowOffset(scrollOffset);
      });
    };
  },
};

/**
 * Helper function to identify the ROW_DETAILS_COLUMN
 * @internal
 */
export function isRowDetailsColumnDef<TData extends DataTableV2RowData>(
  column: ColumnDef<TData>,
): boolean {
  return column.id === ROW_DETAILS_COLUMN.id;
}

/**
 * Column definition for the row details column.
 * @internal
 */
export const ROW_DETAILS_COLUMN: ColumnDef<unknown, unknown> = {
  id: 'DataTableV2RowDetailsColumn',
  alignment: 'left',
  accessorFn: () => '',
  header: () => {
    return '';
  },
  width: 'content',
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    builtinPlacement: 'prefix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    cellDeps: (cell) => {
      return [cell.row.getIsDetailsOpen()];
    },
  },
  cell: ({ row }) => {
    // This is actually a react component, only the definition is not using an
    // uppercase `Cell` name, which confuses the lint rule
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const intl = useIntl();

    const isOpen = row.getIsDetailsOpen();

    // This is actually a react component, only the definition is not using an
    // uppercase `Cell` name, which confuses the lint rule
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const Icon = useMemo(() => {
      return isOpen ? <ChevronDownSmallIcon /> : <ChevronRightSmallIcon />;
    }, [isOpen]);

    return (
      <DataTableV2DefaultCell>
        <Button
          size="condensed"
          variant="default"
          style={{
            borderRadius: Borders.Radius.Field.Subdued,
            paddingLeft: Spacings.Size2,
            paddingRight: Spacings.Size2,
            paddingTop: 0,
            paddingBottom: 0,
          }}
          aria-label={intl.formatMessage(
            {
              defaultMessage:
                '{isDetailsOpen, select, true {Collapse} other {Expand}} row with id {id}',
              id: '8a6rHEksmq2fZCQl',
              description:
                'Aria label used for data table expandable row trigger.',
            },
            {
              isDetailsOpen: isOpen,
              id: row.id,
            },
          )}
          aria-expanded={isOpen}
          aria-controls={row.getRowDetailsAriaControlsId()}
          onClick={() => row.toggleDetails()}
          disabled={!row.getCanOpenDetails()}
        >
          <Button.Prefix>{Icon}</Button.Prefix>
        </Button>
      </DataTableV2DefaultCell>
    );
  },
};

/**
 * Configuration hook for subrows of the DataTableV2.
 * @internal
 */
export function useRowDetails<TData extends DataTableV2RowData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
) {
  const { children } = props;
  const rowDetailsTemplateProps = useMemo(() => {
    for (const child of Children.toArray(children)) {
      if (isDataTableV2ExpandableRowTemplate<TData>(child)) {
        return child?.props;
      }
    }
  }, [children]);

  const [openRowDetails, setOpenRowDetails] = useControllableState({
    value: rowDetailsTemplateProps?.expandedRows,
    defaultValue: rowDetailsTemplateProps?.defaultExpandedRows,
    onChange: (value) => {
      if (value !== true) {
        rowDetailsTemplateProps?.onExpandedRowsChange?.(value);
      }
    },
  });

  const template = rowDetailsTemplateProps?.children;
  if (template !== undefined && typeof template === 'function') {
    options.enableRowDetails = true;
    options.rowDetailsTemplate = template;
    options.getRowDetailsRowModel = getRowDetailsRowModel();
    options.rowDetailsDisableRow = rowDetailsTemplateProps?.disableExpand;

    options.state = {
      ...options.state,
      rowDetails: openRowDetails,
    };
    options.onRowDetailsOpenChange = setOpenRowDetails;
  }
}
